'use client';

import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactFlowProvider } from 'reactflow';
import { X } from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { WorkflowReadOnlyCanvas } from '../components/workflow/WorkflowReadOnlyCanvas';
import { workflowApi } from '../services/api';
import type { WorkflowVersion } from '../types';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function paramValueStr(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function NodeParamsDiffPanel({
  leftConfig,
  rightConfig,
  leftExists,
  rightExists,
  t,
}: {
  leftConfig: Record<string, unknown> | null | undefined;
  rightConfig: Record<string, unknown> | null | undefined;
  leftExists: boolean;
  rightExists: boolean;
  t: (key: string, opts?: { defaultValue?: string }) => string;
}) {
  const leftParams = ((): Record<string, unknown> => {
    const c = leftConfig;
    if (!c) return {};
    if (c.params && typeof c.params === 'object') return c.params as Record<string, unknown>;
    if (typeof c === 'object' && c !== null && !('type' in c)) return c as Record<string, unknown>;
    return {};
  })();
  const rightParams = ((): Record<string, unknown> => {
    const c = rightConfig;
    if (!c) return {};
    if (c.params && typeof c.params === 'object') return c.params as Record<string, unknown>;
    if (typeof c === 'object' && c !== null && !('type' in c)) return c as Record<string, unknown>;
    return {};
  })();
  const allKeys = useMemo(() => {
    const keys = new Set<string>([...Object.keys(leftParams), ...Object.keys(rightParams)]);
    return Array.from(keys).sort();
  }, [leftParams, rightParams]);

  if (!leftExists && !rightExists) return null;
  if (!leftExists) {
    return (
      <div className="grid grid-cols-2 gap-4 flex-1 min-w-0">
        <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200">
          <p className="text-xs font-medium text-zinc-500 mb-2">{t('workflow.nodeParamsLeft')}</p>
          <p className="text-xs text-zinc-400">{t('workflow.onlyInRight')}</p>
        </div>
        <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200 overflow-auto">
          <p className="text-xs font-medium text-zinc-500 mb-2">{t('workflow.nodeParamsRight')}</p>
          <ParamList params={rightParams} />
        </div>
      </div>
    );
  }
  if (!rightExists) {
    return (
      <div className="grid grid-cols-2 gap-4 flex-1 min-w-0">
        <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200 overflow-auto">
          <p className="text-xs font-medium text-zinc-500 mb-2">{t('workflow.nodeParamsLeft')}</p>
          <ParamList params={leftParams} />
        </div>
        <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200">
          <p className="text-xs font-medium text-zinc-500 mb-2">{t('workflow.nodeParamsRight')}</p>
          <p className="text-xs text-zinc-400">{t('workflow.onlyInLeft')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 flex-1 min-w-0 overflow-hidden">
      <div className="flex flex-col min-w-0">
        <p className="text-xs font-medium text-zinc-500 mb-2 px-1">{t('workflow.nodeParamsLeft')}</p>
        <div className="flex-1 overflow-auto rounded-lg border border-zinc-200 bg-zinc-50 p-2 space-y-0.5">
          {allKeys.map((key) => {
            const leftVal = leftParams[key];
            const rightVal = rightParams[key];
            const leftStr = paramValueStr(leftVal);
            const rightStr = paramValueStr(rightVal);
            const onlyLeft = !(key in rightParams);
            const onlyRight = !(key in leftParams);
            const diff = onlyLeft || onlyRight || leftStr !== rightStr;
            return (
              <div
                key={key}
                className={`flex gap-2 text-xs rounded px-2 py-1 ${diff ? 'bg-amber-50 border-l-2 border-amber-400' : ''}`}
              >
                <dt className="text-zinc-500 font-mono shrink-0">{key}:</dt>
                <dd className="text-zinc-800 break-all flex-1 min-w-0">{leftStr || '—'}</dd>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col min-w-0">
        <p className="text-xs font-medium text-zinc-500 mb-2 px-1">{t('workflow.nodeParamsRight')}</p>
        <div className="flex-1 overflow-auto rounded-lg border border-zinc-200 bg-zinc-50 p-2 space-y-0.5">
          {allKeys.map((key) => {
            const leftVal = leftParams[key];
            const rightVal = rightParams[key];
            const leftStr = paramValueStr(leftVal);
            const rightStr = paramValueStr(rightVal);
            const onlyLeft = !(key in rightParams);
            const onlyRight = !(key in leftParams);
            const diff = onlyLeft || onlyRight || leftStr !== rightStr;
            return (
              <div
                key={key}
                className={`flex gap-2 text-xs rounded px-2 py-1 ${diff ? 'bg-amber-50 border-l-2 border-amber-400' : ''}`}
              >
                <dt className="text-zinc-500 font-mono shrink-0">{key}:</dt>
                <dd className="text-zinc-800 break-all flex-1 min-w-0">{rightStr || '—'}</dd>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ParamList({ params }: { params: Record<string, unknown> }) {
  const entries = Object.entries(params);
  if (entries.length === 0) return <p className="text-xs text-zinc-400">—</p>;
  return (
    <dl className="space-y-1.5 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <dt className="text-zinc-500 font-mono shrink-0">{key}:</dt>
          <dd className="text-zinc-800 break-all">
            {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '')}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function WorkflowVersionDiffPage() {
  const { t } = useTranslation();
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const leftId = searchParams.get('left') ?? '';
  const rightId = searchParams.get('right') ?? '';

  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [leftVersion, setLeftVersion] = useState<WorkflowVersion | null>(null);
  const [rightVersion, setRightVersion] = useState<WorkflowVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  /* 挂载时给 body 加类，便于 CSS 选择器命中 ReactFlow 的 default 节点（可能不在画布容器内） */
  useEffect(() => {
    document.body.classList.add('workflow-diff-page');
    return () => document.body.classList.remove('workflow-diff-page');
  }, []);

  useEffect(() => {
    if (!workflowId) return;
    workflowApi
      .listWorkflowVersions(workflowId)
      .then((res) => {
        const list = res?.data?.versions ?? [];
        setVersions(list);
        if (list.length === 0) return;
        const l = leftId ? list.find((v) => v.id === leftId) : list[0];
        const r = rightId ? list.find((v) => v.id === rightId) : (list[1] ?? list[0]);
        setLeftVersion(l ?? null);
        setRightVersion(r ?? null);
      })
      .finally(() => setLoading(false));
  }, [workflowId, leftId, rightId]);

  const leftNode = useMemo(
    () => leftVersion?.nodes?.find((n) => n.id === selectedNodeId),
    [leftVersion?.nodes, selectedNodeId]
  );
  const rightNode = useMemo(
    () => rightVersion?.nodes?.find((n) => n.id === selectedNodeId),
    [rightVersion?.nodes, selectedNodeId]
  );

  const handleNodeSelect = (nodeId: string) => setSelectedNodeId(nodeId);

  const setLeftVersionId = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('left', id);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };
  const setRightVersionId = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('right', id);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  if (!workflowId) {
    return (
      <div className="h-screen flex items-center justify-center text-zinc-500">
        {t('workflow.noVersions')}
      </div>
    );
  }

  if (loading || !leftVersion || !rightVersion) {
    return (
      <div className="h-screen flex items-center justify-center text-zinc-500">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const leftConfig = leftNode?.config as Record<string, unknown> | undefined;
  const rightConfig = rightNode?.config as Record<string, unknown> | undefined;

  return (
    <div className="h-screen flex flex-col bg-zinc-100">
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-white border-b border-zinc-200">
        <h1 className="text-sm font-semibold text-zinc-900">{t('workflow.compareVersions')}</h1>
        <div className="flex items-center gap-2">
          <select
            className="rounded-lg border border-zinc-200 px-3 py-1.5 text-sm bg-white min-w-[180px]"
            value={leftVersion.id}
            onChange={(e) => setLeftVersionId(e.target.value)}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {t('workflow.versionNumber', { number: v.versionNumber })} — {formatDate(v.createdAt)}
              </option>
            ))}
          </select>
          <span className="text-zinc-400 text-sm">vs</span>
          <select
            className="rounded-lg border border-zinc-200 px-3 py-1.5 text-sm bg-white min-w-[180px]"
            value={rightVersion.id}
            onChange={(e) => setRightVersionId(e.target.value)}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {t('workflow.versionNumber', { number: v.versionNumber })} — {formatDate(v.createdAt)}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<X className="w-4 h-4" />}
          onClick={() => navigate(`/workflows/store/edit/${workflowId}`)}
        >
          {t('workflow.closeDiff')}
        </Button>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 flex min-w-0 min-h-[300px]">
          <div className="flex-1 min-w-0 flex flex-col border-r border-zinc-200 bg-white min-h-0">
            <p className="text-xs text-zinc-500 px-2 py-1 border-b border-zinc-100 flex-shrink-0">
              {t('workflow.selectVersionLeft')} — {t('workflow.versionNumber', { number: leftVersion.versionNumber })}
            </p>
            <div className="flex-1 min-h-[200px] overflow-hidden">
              <ReactFlowProvider>
                <WorkflowReadOnlyCanvas
                  nodes={leftVersion.nodes ?? []}
                  edges={leftVersion.edges ?? []}
                  onNodeSelect={handleNodeSelect}
                  selectedNodeId={selectedNodeId}
                />
              </ReactFlowProvider>
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col bg-white min-h-0">
            <p className="text-xs text-zinc-500 px-2 py-1 border-b border-zinc-100 flex-shrink-0">
              {t('workflow.selectVersionRight')} — {t('workflow.versionNumber', { number: rightVersion.versionNumber })}
            </p>
            <div className="flex-1 min-h-[200px] overflow-hidden">
              <ReactFlowProvider>
                <WorkflowReadOnlyCanvas
                  nodes={rightVersion.nodes ?? []}
                  edges={rightVersion.edges ?? []}
                  onNodeSelect={handleNodeSelect}
                  selectedNodeId={selectedNodeId}
                />
              </ReactFlowProvider>
            </div>
          </div>
        </div>

        {/* 底部：点击节点后显示左右版本参数对比，有差异时高亮；固定高度保证内容可见 */}
        <div className="flex-shrink-0 border-t border-zinc-200 bg-white flex flex-col min-h-[260px] max-h-[320px]">
          {selectedNodeId ? (
            <>
              <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-zinc-100">
                <p className="text-xs font-semibold text-zinc-700">
                  {t('workflow.nodeParams')} — <span className="font-mono text-zinc-500">{selectedNodeId}</span>
                </p>
              </div>
              <div className="flex-1 min-h-[200px] overflow-auto px-3 py-2 flex">
                {leftNode || rightNode ? (
                  <NodeParamsDiffPanel
                    leftConfig={leftConfig ?? null}
                    rightConfig={rightConfig ?? null}
                    leftExists={!!leftNode}
                    rightExists={!!rightNode}
                    t={t}
                  />
                ) : (
                  <p className="text-xs text-zinc-400 self-center">{t('workflow.noConfig')}</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-xs text-zinc-400 px-3 py-4 text-center flex-shrink-0">{t('workflow.nodeParamsCompareHint')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
